import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Pencil, Trash2, MoveRight, History, Package } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { PathTrail } from "@/components/PathTrail";
import { LocationSelect } from "@/components/Selects";
import { UpgradeHint } from "@/components/UpgradeHint";
import { isLow, LowBadge } from "@/components/ItemCard";
import { api, fileUrl } from "@/lib/api";
import { useCategories, useCatLabel, useErrorToast } from "@/lib/hooks";
import { useAuth, useWs } from "@/context/Auth";
import { useI18n } from "@/i18n/I18n";

const HistoryList = ({ id }) => {
  const { t, fmtDateTime } = useI18n();
  const { data = [] } = useQuery({ queryKey: ["item", id, "history"], queryFn: () => api.get(`/items/${id}/history`).then((r) => r.data) });
  if (!data.length) return <p className="text-sm text-slate-400">{t("item.historyEmpty")}</p>;
  return (
    <ol className="relative border-s-2 border-slate-200 ms-2 space-y-5" data-testid="item-history">
      {data.map((m) => (
        <li key={m.id} className="ps-5 relative">
          <span className="absolute -start-[7px] top-1.5 h-3 w-3 rounded-full bg-blue-600 ring-4 ring-white" />
          <p className="font-mono text-xs text-slate-500">{m.from_path.join(" → ") || "—"}</p>
          <p className="font-mono text-sm text-slate-900">⇒ {m.to_path.join(" → ") || "—"}</p>
          <p className="text-xs text-slate-400 mt-0.5">{fmtDateTime(m.created_at)} · {t("item.movedBy", { user: m.user_name })}</p>
        </li>
      ))}
    </ol>
  );
};

const MoveDialog = ({ item, open, onOpenChange }) => {
  const { t } = useI18n();
  const qc = useQueryClient();
  const showErr = useErrorToast();
  const [to, setTo] = useState(item.location_id);
  const move = async () => {
    try {
      await api.post(`/items/${item.id}/move`, { location_id: to });
      ["item", "items", "locations", "location", "movements"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
      toast.success(t("item.moved"));
      onOpenChange(false);
    } catch (e) { showErr(e); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="move-dialog">
        <DialogHeader><DialogTitle>{t("item.moveTo")}</DialogTitle></DialogHeader>
        <PathTrail path={item.path} linkable={false} emptyLabel={t("common.noLocation")} />
        <LocationSelect value={to} onChange={setTo} testId="move-location-select" />
        <DialogFooter><Button data-testid="move-confirm-button" className="h-11" onClick={move}>{t("item.move")}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default function ItemDetail() {
  const { id } = useParams();
  const { t, fmtNum, fmtDate } = useI18n();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { refresh } = useAuth();
  const { canWrite, features } = useWs();
  const showErr = useErrorToast();
  const [moving, setMoving] = useState(false);
  const { data: item, isLoading, isError } = useQuery({ queryKey: ["item", id], queryFn: () => api.get(`/items/${id}`).then((r) => r.data) });
  const { data: cats = [] } = useCategories();
  const catLabel = useCatLabel();
  if (isLoading) return <p className="text-slate-500">{t("common.loading")}</p>;
  if (isError) return <p data-testid="item-not-found">404</p>;
  const cat = cats.find((c) => c.id === item.category_id);

  const remove = async () => {
    if (!window.confirm(t("common.confirmDelete"))) return;
    try { await api.delete(`/items/${id}`); qc.invalidateQueries(); refresh(); navigate("/items"); } catch (e) { showErr(e); }
  };

  return (
    <div className="grid md:grid-cols-[minmax(0,360px)_1fr] gap-8 fade-up">
      <div className="aspect-square w-full overflow-hidden rounded-2xl border border-slate-200 bg-slate-100 flex items-center justify-center">
        {item.photo_path ? <img src={fileUrl(item.photo_path)} alt={item.name} className="h-full w-full object-cover" data-testid="item-photo" /> : <Package className="w-16 h-16 text-slate-300" />}
      </div>
      <div className="space-y-6 min-w-0">
        <div className="space-y-2">
          {cat && <p className="text-xs font-medium uppercase tracking-wider text-slate-500" data-testid="item-category">{catLabel(cat)}</p>}
          <h1 data-testid="item-title" className="text-3xl sm:text-4xl font-extrabold break-words">{item.name}</h1>
          <p className="flex flex-wrap items-center gap-2 font-mono text-sm text-slate-500" data-testid="item-quantity">
            {t("common.quantity")} : {fmtNum(item.quantity)}{item.min_quantity != null && <span className="text-slate-400">· {t("stock.min")} : {fmtNum(item.min_quantity)}</span>}
            {isLow(item) && <LowBadge testId="item-low-badge" />}
          </p>
        </div>
        <section className="rounded-2xl border-2 border-blue-100 bg-blue-50/50 p-5 space-y-3">
          <p className="text-xs font-semibold uppercase tracking-wider text-blue-800">{t("item.where")}</p>
          <PathTrail path={item.path} size="lg" emptyLabel={t("common.noLocation")} testId="item-path" />
        </section>
        {canWrite && (
          <div className="flex flex-wrap gap-2">
            <Button data-testid="item-move-button" className="h-12 gap-2 px-6" onClick={() => setMoving(true)}><MoveRight className="w-4 h-4 rtl:rotate-180" />{t("item.move")}</Button>
            <Button data-testid="item-edit-button" variant="outline" className="h-12 gap-2" asChild><Link to={`/items/${id}/edit`}><Pencil className="w-4 h-4" />{t("common.edit")}</Link></Button>
            <Button data-testid="item-delete-button" variant="ghost" className="h-12 gap-2 text-red-600" onClick={remove}><Trash2 className="w-4 h-4" />{t("common.delete")}</Button>
          </div>
        )}
        {item.description && <p className="text-slate-700 whitespace-pre-wrap" data-testid="item-description">{item.description}</p>}
        {item.notes && <div className="rounded-lg bg-amber-50 border border-amber-100 p-4 text-sm text-slate-700 whitespace-pre-wrap" data-testid="item-notes">{item.notes}</div>}
        <p className="text-xs text-slate-400">{fmtDate(item.created_at)} · {item.created_by}</p>
        <section className="space-y-4 pt-2">
          <h2 className="flex items-center gap-2 text-lg font-bold"><History className="w-4 h-4" />{t("item.history")}</h2>
          {features.history ? <HistoryList id={id} /> : <UpgradeHint text={t("item.historyLocked")} testId="history-locked" />}
        </section>
      </div>
      {moving && <MoveDialog item={item} open={moving} onOpenChange={setMoving} />}
    </div>
  );
}
